import { OktaAuth, OktaAuthOptions } from '@okta/okta-auth-js';
import axiosRetry, { isRetryableError } from 'axios-retry';
import axios, { AxiosError } from 'axios';

export interface IdentityOktaAuthOptions extends OktaAuthOptions {
  onRetry?: (error: AxiosError) => Promise<void>;
  errorHandler?: (error: AxiosError) => Promise<void>;
}

export const createOktaAuth = (config: IdentityOktaAuthOptions): OktaAuth => {
  const axiosInstance = axios.create();
  axiosRetry(axiosInstance, {
    retries: 3,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: (error) => {
      return isRetryableError(error) || (error.response?.status === 429);
    },
    onRetry: async (_, error) => await config.onRetry?.(error),
  });
  const httpRequestClient = async (method, url, args) => {
    try {
      const { headers, data, withCredentials } = args;
      const contentType = headers?.['Content-Type'] || headers?.['content-type'];

      let dataToSend = data;
      if (contentType === 'application/json' && data && typeof data !== 'string') {
        dataToSend = JSON.stringify(data);
      }
      const response = await axiosInstance({
        method,
        headers,
        url,
        data: dataToSend,
        withCredentials,
      });
      // okta-auth-js library expects the XMLHttpRequest object to
      // be returned which contains response from the server as well
      return response.request;
    }
    catch (err) {
      await config.errorHandler?.(err);
      throw err;
    }
  };

  const oktaAuth = new OktaAuth({ httpRequestClient, ...config });
  return oktaAuth;
};
